import React, { useState, useEffect } from "react";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  AlertTriangle,
  Trash2,
  FileX,
  ArrowRight,
  Info,
  Shield,
  CheckCircle,
  X
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from "../../../../utils/ApiUtils";
import swal from "sweetalert";
import { toast } from "sonner";

// Helper function to format numbers as Swedish currency (SEK)
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};

const DeleteVerificationModal = ({ show, handleClose, verificationData, companyId, onVerificationDeleted }) => {
  const [annulmentTransactions, setAnnulmentTransactions] = useState([]);
  const [allowFullDeletion, setAllowFullDeletion] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    generateAnnulmentTransactions();
  }, [verificationData]);

  // Generate annulment transactions
  const generateAnnulmentTransactions = () => {
    if (verificationData && verificationData.transactions) {
      const reversedTransactions = verificationData.transactions.map((transaction) => ({
        ...transaction,
        transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
      }));
      setAnnulmentTransactions(reversedTransactions);
    }
  };

  const handleDelete = async () => {
    try {
      setIsProcessing(true);

      // Send DELETE request with the `allowFullDeletion` flag in the request body
      await axios.delete(getApiUrl(`${companyId}/verifications/${verificationData.verification_id}`), {
        data: { allowFullDeletion },
        withCredentials: true,
      });

      if (allowFullDeletion) {
        toast.success("Verifikatet är nu helt raderat");
      } else {
        toast.success("Verifikatet har nu blivit annulerat");
      }

      // Notify parent component that a verification was deleted/annulled
      if (onVerificationDeleted) {
        onVerificationDeleted();
      }

      handleClose();
    } catch (error) {
      console.error('Error deleting verification:', error);
      toast.error(error.response?.data?.message || "Det gick inte att radera verifikatet");
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate totals for original verification
  // Convert amounts to numbers to ensure proper addition (not string concatenation)
  const originalDebitTotal = verificationData?.transactions?.reduce((sum, t) => {
    const amount = t.transaction_type === 'debit' ? Number(t.amount) || 0 : 0;
    return sum + amount;
  }, 0) || 0;
  
  const originalCreditTotal = verificationData?.transactions?.reduce((sum, t) => {
    const amount = t.transaction_type === 'credit' ? Number(t.amount) || 0 : 0;
    return sum + amount;
  }, 0) || 0;

  // Calculate totals for annulment verification
  const annulmentDebitTotal = annulmentTransactions.reduce((sum, t) => {
    const amount = t.transaction_type === 'debit' ? Number(t.amount) || 0 : 0;
    return sum + amount;
  }, 0);
  
  const annulmentCreditTotal = annulmentTransactions.reduce((sum, t) => {
    const amount = t.transaction_type === 'credit' ? Number(t.amount) || 0 : 0;
    return sum + amount;
  }, 0);

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <style>{`
        .compact-modal .modal-dialog {
          max-width: 800px;
        }
        
        .delete-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .process-flow {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 1.5rem 0;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        .process-step {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 0.75rem;
          text-align: center;
          min-width: 140px;
          flex: 1;
          max-width: 180px;
        }
        
        .process-step.original {
          border-color: #dc2626;
          background: #fef2f2;
        }
        
        .process-step.reversal {
          border-color: #059669;
          background: #ecfdf5;
        }
        
        .process-arrow {
          color: #64748b;
          font-size: 1.2rem;
        }
        
        .compact-table {
          background: white;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          margin-bottom: 1rem;
        }
        
        .compact-table-header {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 0.5rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          color: #64748b;
        }
        
        .compact-table-row {
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.875rem;
          transition: background-color 0.15s ease;
        }
        
        .compact-table-row:hover {
          background: #f8fafc;
        }
        
        .compact-table-row:last-child {
          border-bottom: none;
        }
        
        .compact-table-footer {
          background: #f1f5f9;
          padding: 0.5rem 0.75rem;
          font-weight: 600;
          font-size: 0.875rem;
          border-top: 1px solid #e2e8f0;
        }
        
        .amount-cell {
          font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
          font-size: 0.8rem;
        }
        
        .option-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        
        .option-card:hover {
          border-color: #3b82f6;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
        }
        
        .option-card.selected {
          border-color: #3b82f6;
          background: #f8fafc;
        }
        
        .option-card.danger {
          border-color: #dc2626;
          background: #fef2f2;
        }
        
        .option-card.danger:hover {
          border-color: #b91c1c;
        }
        
        .table-section {
          margin-bottom: 1.5rem;
        }
        
        .section-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .verification-tables {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        @media (max-width: 768px) {
          .verification-tables {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="compact-modal">
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="h5 fw-medium d-flex align-items-center">
            <Trash2 className="me-2" size={20} style={{ color: '#dc2626' }} />
            Radera Verifikat {verificationData?.series}{verificationData?.number}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-3">
          {/* Warning Header */}
          <div className="delete-warning">
            <div className="d-flex align-items-center">
              <AlertTriangle size={18} className="me-2" />
              <div>
                <div className="fw-medium mb-1">Varning: Du håller på att radera ett verifikat</div>
                <div className="small opacity-75">
                  Detta skapar en annuleringsverifikation som tar bort effekten av det ursprungliga verifikatet.
                </div>
              </div>
            </div>
          </div>

          {/* Process Flow */}
          <div className="process-flow">
            <div className="process-step original">
              <div className="d-flex align-items-center justify-content-center mb-1">
                <FileX size={16} className="text-danger" />
              </div>
              <div className="fw-medium text-danger small">Ursprungligt</div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {verificationData?.series}{verificationData?.number}
              </div>
            </div>

            <ArrowRight className="process-arrow" size={18} />

            <div className="process-step reversal">
              <div className="d-flex align-items-center justify-content-center mb-1">
                <CheckCircle size={16} className="text-success" />
              </div>
              <div className="fw-medium text-success small">Annullering</div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                Skapas automatiskt
              </div>
            </div>
          </div>

          {/* Verification Tables */}
          <div className="verification-tables">
            {/* Original Verification */}
            <div className="table-section">
              <div className="section-title">
                <FileX size={16} className="text-danger" />
                Ursprungligt Verifikat
              </div>
              
              <div className="compact-table">
                <div className="compact-table-header d-flex">
                  <div style={{ flex: '2' }}>Konto</div>
                  <div style={{ flex: '1', textAlign: 'right' }}>Debet</div>
                  <div style={{ flex: '1', textAlign: 'right' }}>Kredit</div>
                </div>
                
                {verificationData?.transactions?.map((transaction, index) => (
                  <div key={index} className="compact-table-row d-flex align-items-center">
                    <div style={{ flex: '2' }}>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{transaction.account_name}</Tooltip>}
                      >
                        <span className="fw-medium">{transaction.account_number}</span>
                      </OverlayTrigger>
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                      {transaction.transaction_type === 'debit' ? (
                        <span className="text-success fw-medium">{formatCurrency(transaction.amount)}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                      {transaction.transaction_type === 'credit' ? (
                        <span className="text-primary fw-medium">{formatCurrency(transaction.amount)}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </div>
                ))}
                
                <div className="compact-table-footer d-flex">
                  <div style={{ flex: '2' }}>Summa</div>
                  <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
                    {formatCurrency(originalDebitTotal)}
                  </div>
                  <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
                    {formatCurrency(originalCreditTotal)}
                  </div>
                </div>
              </div>
            </div>

            {/* Annulment Verification */}
            <div className="table-section">
              <div className="section-title">
                <CheckCircle size={16} className="text-success" />
                Annuleringsverifikat
              </div>
              
              <div className="compact-table">
                <div className="compact-table-header d-flex">
                  <div style={{ flex: '2' }}>Konto</div>
                  <div style={{ flex: '1', textAlign: 'right' }}>Debet</div>
                  <div style={{ flex: '1', textAlign: 'right' }}>Kredit</div>
                </div>
                
                {annulmentTransactions.map((transaction, index) => (
                  <div key={index} className="compact-table-row d-flex align-items-center">
                    <div style={{ flex: '2' }}>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{transaction.account_name}</Tooltip>}
                      >
                        <span className="fw-medium">{transaction.account_number}</span>
                      </OverlayTrigger>
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                      {transaction.transaction_type === 'debit' ? (
                        <span className="text-success fw-medium">{formatCurrency(transaction.amount)}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                      {transaction.transaction_type === 'credit' ? (
                        <span className="text-primary fw-medium">{formatCurrency(transaction.amount)}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </div>
                ))}
                
                <div className="compact-table-footer d-flex">
                  <div style={{ flex: '2' }}>Summa</div>
                  <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
                    {formatCurrency(annulmentDebitTotal)}
                  </div>
                  <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
                    {formatCurrency(annulmentCreditTotal)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Deletion Options */}
          <div className="table-section">
            <div className="section-title">
              <Shield size={16} />
              Raderingsalternativ
            </div>

            {/* Standard Annulment Option */}
            <div
              className={`option-card ${!allowFullDeletion ? 'selected' : ''}`}
              onClick={() => setAllowFullDeletion(false)}
            >
              <div className="d-flex align-items-start">
                <div className="me-2 mt-1">
                  <div className={`rounded-circle d-flex align-items-center justify-content-center ${!allowFullDeletion ? 'bg-primary' : 'bg-light'}`}
                    style={{ width: '16px', height: '16px', minWidth: '16px' }}>
                    {!allowFullDeletion && <div className="bg-white rounded-circle" style={{ width: '6px', height: '6px' }}></div>}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-medium mb-1 d-flex align-items-center small">
                    <CheckCircle size={14} className="me-1 text-success" />
                    Annullering (Rekommenderat)
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                    Skapar ett annuleringsverifikat som behåller revisionsspår och följer redovisningspraxis.
                  </p>
                </div>
              </div>
            </div>

            {/* Full Deletion Option */}
            <div
              className={`option-card danger ${allowFullDeletion ? 'selected' : ''}`}
              onClick={() => setAllowFullDeletion(true)}
            >
              <div className="d-flex align-items-start">
                <div className="me-2 mt-1">
                  <div className={`rounded-circle d-flex align-items-center justify-content-center ${allowFullDeletion ? 'bg-danger' : 'bg-light'}`}
                    style={{ width: '16px', height: '16px', minWidth: '16px' }}>
                    {allowFullDeletion && <div className="bg-white rounded-circle" style={{ width: '6px', height: '6px' }}></div>}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-medium mb-1 d-flex align-items-center small">
                    <AlertTriangle size={14} className="me-1 text-warning" />
                    Fullständig Radering (Endast Development)
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Permanent radering som inte följer redovisningspraxis.
                          Ska endast användas under utveckling eller testmiljöer.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="ms-1 text-muted" size="sm" />
                    </OverlayTrigger>
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                    Raderar permanent utan annuleringsverifikation. Förstör revisionsspår - endast för utveckling.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Final Warning */}
          <div className="alert alert-warning py-2 px-3 d-flex align-items-center" style={{ fontSize: '0.875rem' }}>
            <AlertTriangle size={16} className="me-2 flex-shrink-0" />
            <div>
              <strong>Varning:</strong> Denna åtgärd kan inte ångras.
              {allowFullDeletion ?
                " Verifikatet kommer att raderas permanent." :
                " Ett annuleringsverifikat kommer att skapas."
              }
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-2">
          <div className="d-flex gap-2 w-100 justify-content-end">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              disabled={isProcessing}
              size="sm"
            >
              <X size={14} className="me-1" />
              Avbryt
            </Button>

            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isProcessing}
              size="sm"
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                  {allowFullDeletion ? "Raderar..." : "Annullerar..."}
                </>
              ) : (
                <>
                  <Trash2 size={14} className="me-1" />
                  {allowFullDeletion ? "Radera Verifikat" : "Annullera Verifikat"}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default DeleteVerificationModal;


// import React, { useState, useEffect } from "react";
// import { Modal, Button, Table, Form, Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faInfoCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
// import {
//   AlertTriangle,
//   Trash2,
//   FileX,
//   ArrowRight,
//   Calculator,
//   Info,
//   Shield,
//   CheckCircle,
//   X
// } from 'lucide-react';
// import axios from '../../../../services/AxiosInstance';
// import { getApiUrl } from "../../../../utils/ApiUtils";
// import swal from "sweetalert";

// // Helper function to format numbers as Swedish currency (SEK)
// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
// };

// const DeleteVerificationModal = ({ show, handleClose, verificationData, companyId, onVerificationDeleted }) => {
//   const [annulmentTransactions, setAnnulmentTransactions] = useState([]);
//   const [allowFullDeletion, setAllowFullDeletion] = useState(false);
//   const [isProcessing, setIsProcessing] = useState(false);

//   useEffect(() => {
//     generateAnnulmentTransactions();
//   }, [verificationData]);

//   // Generate annulment transactions
//   const generateAnnulmentTransactions = () => {
//     if (verificationData && verificationData.transactions) {
//       const reversedTransactions = verificationData.transactions.map((transaction) => ({
//         ...transaction,
//         transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
//       }));
//       setAnnulmentTransactions(reversedTransactions);
//     }
//   };

//   const handleDelete = async () => {
//     try {
//       setIsProcessing(true);

//       // Send DELETE request with the `allowFullDeletion` flag in the request body
//       await axios.delete(getApiUrl(`${companyId}/verifications/${verificationData.verification_id}`), {
//         data: { allowFullDeletion },  // Pass the checkbox value
//         withCredentials: true,
//       });

//       if (allowFullDeletion) {
//         swal("Raderat!", "Verifikatet är nu helt raderat", "success");
//       } else {
//         swal("Annulerat!", "Verifikatet har nu blivit annulerat", "success");
//       }

//       // Notify parent component that a verification was deleted/annulled
//       if (onVerificationDeleted) {
//         onVerificationDeleted();
//       }

//       handleClose();
//     } catch (error) {
//       console.error('Error deleting verification:', error);
//       swal("Fel", error.response?.data?.message || "Det gick inte att radera verifikatet", "error");
//     } finally {
//       setIsProcessing(false);
//     }
//   };

//   // Calculate totals for original verification
//   const originalDebitTotal = verificationData?.transactions?.reduce((sum, t) =>
//     sum + (t.transaction_type === 'debit' ? t.amount : 0), 0) || 0;
//   const originalCreditTotal = verificationData?.transactions?.reduce((sum, t) =>
//     sum + (t.transaction_type === 'credit' ? t.amount : 0), 0) || 0;

//   // Calculate totals for annulment verification
//   const annulmentDebitTotal = annulmentTransactions.reduce((sum, t) =>
//     sum + (t.transaction_type === 'debit' ? t.amount : 0), 0);
//   const annulmentCreditTotal = annulmentTransactions.reduce((sum, t) =>
//     sum + (t.transaction_type === 'credit' ? t.amount : 0), 0);

//   return (
//     <Modal show={show} onHide={handleClose} size="xl" centered>
//       <Modal.Header closeButton className="border-0 pb-0">
//         <Modal.Title className="h4 fw-medium">
//           <div className="d-flex align-items-center">
//             <Trash2 className="me-3" size={28} style={{ color: '#dc2626' }} />
//             <div>
//               <div className="fw-medium mb-1">
//                 Radera Verifikat {verificationData?.series}{verificationData?.number}
//               </div>
//               <div className="text-muted fw-normal small">
//                 {verificationData?.description}
//               </div>
//             </div>
//           </div>
//         </Modal.Title>
//       </Modal.Header>

//       <Modal.Body className="p-4">
//         <style>{`
//           .delete-section {
//             background: #f8fafc;
//             border-radius: 12px;
//             padding: 1.5rem;
//             margin-bottom: 1.5rem;
//             border: 1px solid #e2e8f0;
//           }
          
//           .section-icon {
//             width: 24px;
//             height: 24px;
//             color: #64748b;
//           }
          
//           .warning-header {
//             background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
//             color: white;
//             border-radius: 12px;
//             padding: 1.5rem;
//             margin-bottom: 1.5rem;
//           }
          
//           .process-flow {
//             display: flex;
//             align-items: center;
//             justify-content: center;
//             margin: 2rem 0;
//             gap: 1rem;
//           }
          
//           .process-step {
//             background: white;
//             border: 2px solid #e2e8f0;
//             border-radius: 12px;
//             padding: 1rem;
//             text-align: center;
//             min-width: 200px;
//           }
          
//           .process-step.original {
//             border-color: #dc2626;
//             background: #fef2f2;
//           }
          
//           .process-step.reversal {
//             border-color: #059669;
//             background: #ecfdf5;
//           }
          
//           .process-arrow {
//             color: #64748b;
//             font-size: 1.5rem;
//           }
          
//           .verification-table {
//             background: white;
//             border-radius: 8px;
//             overflow: hidden;
//             border: 1px solid #e2e8f0;
//           }
          
//           .table-header {
//             background: #f1f5f9;
//             border-bottom: 1px solid #e2e8f0;
//           }
          
//           .amount-cell {
//             font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
//             font-size: 0.875rem;
//           }
          
//           .option-card {
//             background: white;
//             border: 2px solid #e2e8f0;
//             border-radius: 12px;
//             padding: 1.5rem;
//             margin-bottom: 1rem;
//             transition: all 0.2s ease;
//             cursor: pointer;
//           }
          
//           .option-card:hover {
//             border-color: #3b82f6;
//             transform: translateY(-1px);
//             box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
//           }
          
//           .option-card.selected {
//             border-color: #3b82f6;
//             background: #f8fafc;
//           }
          
//           .option-card.danger {
//             border-color: #dc2626;
//             background: #fef2f2;
//           }
          
//           .option-card.danger:hover {
//             border-color: #b91c1c;
//           }
          
//           .action-button {
//             display: flex;
//             align-items: center;
//             gap: 0.5rem;
//             padding: 0.75rem 1.5rem;
//             border-radius: 8px;
//             border: 1px solid #e2e8f0;
//             background: white;
//             color: #374151;
//             text-decoration: none;
//             transition: all 0.2s ease;
//             font-size: 0.875rem;
//             font-weight: 500;
//             cursor: pointer;
//           }
          
//           .action-button:hover:not(:disabled) {
//             transform: translateY(-1px);
//             box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
//           }
          
//           .action-button:disabled {
//             opacity: 0.5;
//             cursor: not-allowed;
//           }
          
//           .action-button.danger {
//             background: #dc2626;
//             color: white;
//             border-color: #dc2626;
//           }
          
//           .action-button.danger:hover:not(:disabled) {
//             background: #b91c1c;
//             border-color: #b91c1c;
//             color: white;
//           }
          
//           .action-button.secondary {
//             background: #6b7280;
//             color: white;
//             border-color: #6b7280;
//           }
          
//           .action-button.secondary:hover:not(:disabled) {
//             background: #4b5563;
//             border-color: #4b5563;
//             color: white;
//           }
//         `}</style>

//         {/* Warning Header */}
//         <div className="warning-header">
//           <div className="d-flex align-items-center">
//             <AlertTriangle size={24} className="me-3" />
//             <div>
//               <h5 className="mb-2 text-white">Varning: Du håller på att radera ett verifikat</h5>
//               <p className="mb-0 text-white-50">
//                 Detta skapar en annuleringsverifikation som tar bort effekten av det ursprungliga verifikatet.
//                 Denna åtgärd kan inte ångras.
//               </p>
//             </div>
//           </div>
//         </div>

//         {/* Process Flow */}
//         <div className="process-flow">
//           <div className="process-step original">
//             <div className="d-flex align-items-center justify-content-center mb-2">
//               <FileX size={20} className="text-danger" />
//             </div>
//             <div className="fw-medium text-danger">Ursprungligt Verifikat</div>
//             <div className="small text-muted">{verificationData?.series}{verificationData?.number}</div>
//           </div>

//           <ArrowRight className="process-arrow" size={24} />

//           <div className="process-step reversal">
//             <div className="d-flex align-items-center justify-content-center mb-2">
//               <CheckCircle size={20} className="text-success" />
//             </div>
//             <div className="fw-medium text-success">Annuleringsverifikat</div>
//             <div className="small text-muted">Skapas automatiskt</div>
//           </div>
//         </div>

//         {/* Original Verification */}
//         <div className="delete-section">
//           <div className="d-flex align-items-center mb-3">
//             <FileX className="section-icon me-2" style={{ color: '#dc2626' }} />
//             <h5 className="mb-0 fw-medium">Ursprungligt Verifikat</h5>
//             <span className="badge bg-danger ms-2">Kommer raderas</span>
//           </div>

//           <div className="verification-table">
//             <div className="table-responsive">
//               <table className="table mb-0">
//                 <thead className="table-header">
//                   <tr>
//                     <th className="border-0 py-3 px-4 fw-medium text-muted">Konto</th>
//                     <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Debet (SEK)</th>
//                     <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Kredit (SEK)</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {verificationData?.transactions?.map((transaction, index) => (
//                     <tr key={index} className={index % 2 === 0 ? 'bg-light bg-opacity-50' : ''}>
//                       <td className="py-3 px-4 border-0">
//                         <div className="fw-medium">{transaction.account_number}</div>
//                         <div className="small text-muted">{transaction.account_name}</div>
//                       </td>
//                       <td className="py-3 px-4 border-0 text-end amount-cell">
//                         {transaction.transaction_type === 'debit' ? (
//                           <span className="fw-medium text-success">{formatCurrency(transaction.amount)}</span>
//                         ) : (
//                           <span className="text-muted">—</span>
//                         )}
//                       </td>
//                       <td className="py-3 px-4 border-0 text-end amount-cell">
//                         {transaction.transaction_type === 'credit' ? (
//                           <span className="fw-medium text-primary">{formatCurrency(transaction.amount)}</span>
//                         ) : (
//                           <span className="text-muted">—</span>
//                         )}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//                 <tfoot className="border-top bg-light">
//                   <tr>
//                     <td className="py-3 px-4 fw-bold">Summa</td>
//                     <td className="py-3 px-4 text-end fw-bold amount-cell text-success">
//                       {formatCurrency(originalDebitTotal)}
//                     </td>
//                     <td className="py-3 px-4 text-end fw-bold amount-cell text-primary">
//                       {formatCurrency(originalCreditTotal)}
//                     </td>
//                   </tr>
//                 </tfoot>
//               </table>
//             </div>
//           </div>
//         </div>

//         {/* Annulment Verification */}
//         <div className="delete-section">
//           <div className="d-flex align-items-center mb-3">
//             <CheckCircle className="section-icon me-2" style={{ color: '#059669' }} />
//             <h5 className="mb-0 fw-medium">Annuleringsverifikat</h5>
//             <span className="badge bg-success ms-2">Kommer skapas</span>
//           </div>

//           <div className="verification-table">
//             <div className="table-responsive">
//               <table className="table mb-0">
//                 <thead className="table-header">
//                   <tr>
//                     <th className="border-0 py-3 px-4 fw-medium text-muted">Konto</th>
//                     <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Debet (SEK)</th>
//                     <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Kredit (SEK)</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {annulmentTransactions.map((transaction, index) => (
//                     <tr key={index} className={index % 2 === 0 ? 'bg-light bg-opacity-50' : ''}>
//                       <td className="py-3 px-4 border-0">
//                         <div className="fw-medium">{transaction.account_number}</div>
//                         <div className="small text-muted">{transaction.account_name}</div>
//                       </td>
//                       <td className="py-3 px-4 border-0 text-end amount-cell">
//                         {transaction.transaction_type === 'debit' ? (
//                           <span className="fw-medium text-success">{formatCurrency(transaction.amount)}</span>
//                         ) : (
//                           <span className="text-muted">—</span>
//                         )}
//                       </td>
//                       <td className="py-3 px-4 border-0 text-end amount-cell">
//                         {transaction.transaction_type === 'credit' ? (
//                           <span className="fw-medium text-primary">{formatCurrency(transaction.amount)}</span>
//                         ) : (
//                           <span className="text-muted">—</span>
//                         )}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//                 <tfoot className="border-top bg-light">
//                   <tr>
//                     <td className="py-3 px-4 fw-bold">Summa</td>
//                     <td className="py-3 px-4 text-end fw-bold amount-cell text-success">
//                       {formatCurrency(annulmentDebitTotal)}
//                     </td>
//                     <td className="py-3 px-4 text-end fw-bold amount-cell text-primary">
//                       {formatCurrency(annulmentCreditTotal)}
//                     </td>
//                   </tr>
//                 </tfoot>
//               </table>
//             </div>
//           </div>
//         </div>

//         {/* Deletion Options */}
//         <div className="delete-section">
//           <div className="d-flex align-items-center mb-3">
//             <Shield className="section-icon me-2" />
//             <h5 className="mb-0 fw-medium">Raderingsalternativ</h5>
//           </div>

//           {/* Standard Annulment Option */}
//           <div
//             className={`option-card ${!allowFullDeletion ? 'selected' : ''}`}
//             onClick={() => setAllowFullDeletion(false)}
//           >
//             <div className="d-flex align-items-start">
//               <div className="me-3 mt-1">
//                 <div className={`rounded-circle d-flex align-items-center justify-content-center ${!allowFullDeletion ? 'bg-primary' : 'bg-light'}`}
//                   style={{ width: '20px', height: '20px' }}>
//                   {!allowFullDeletion && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
//                 </div>
//               </div>
//               <div className="flex-grow-1">
//                 <div className="fw-medium mb-2 d-flex align-items-center">
//                   <CheckCircle size={16} className="me-2 text-success" />
//                   Annullering (Rekommenderat)
//                 </div>
//                 <p className="text-muted mb-2 small">
//                   Skapar ett nytt annuleringsverifikat som tar bort effekten av det ursprungliga verifikatet.
//                   Detta är den korrekta redovisningsmetoden som behåller en fullständig revisionsspår.
//                 </p>
//                 <div className="d-flex align-items-center text-success small">
//                   <Info size={14} className="me-1" />
//                   Behåller revisionsspår och följer redovisningspraxis
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Full Deletion Option */}
//           <div
//             className={`option-card danger ${allowFullDeletion ? 'selected' : ''}`}
//             onClick={() => setAllowFullDeletion(true)}
//           >
//             <div className="d-flex align-items-start">
//               <div className="me-3 mt-1">
//                 <div className={`rounded-circle d-flex align-items-center justify-content-center ${allowFullDeletion ? 'bg-danger' : 'bg-light'}`}
//                   style={{ width: '20px', height: '20px' }}>
//                   {allowFullDeletion && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
//                 </div>
//               </div>
//               <div className="flex-grow-1">
//                 <div className="fw-medium mb-2 d-flex align-items-center">
//                   <AlertTriangle size={16} className="me-2 text-warning" />
//                   Fullständig Radering (Endast Development)
//                   <OverlayTrigger
//                     placement="top"
//                     overlay={
//                       <Tooltip>
//                         Permanent radering som inte följer redovisningspraxis.
//                         Ska endast användas under utveckling eller testmiljöer.
//                       </Tooltip>
//                     }
//                   >
//                     <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" />
//                   </OverlayTrigger>
//                 </div>
//                 <p className="text-muted mb-2 small">
//                   Raderar verifikatet permanent utan att skapa någon annuleringsverifikation.
//                   Detta bryter mot god redovisningssed och ska endast användas i utvecklingsmiljöer.
//                 </p>
//                 <div className="d-flex align-items-center text-danger small">
//                   <AlertTriangle size={14} className="me-1" />
//                   Förstör revisionsspår - använd endast för utveckling
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Final Warning */}
//         <div className="alert alert-warning d-flex align-items-center">
//           <AlertTriangle size={20} className="me-2" />
//           <div>
//             <strong>Varning:</strong> Denna åtgärd kan inte ångras.
//             {allowFullDeletion ?
//               " Verifikatet kommer att raderas permanent från systemet." :
//               " Ett annuleringsverifikat kommer att skapas."
//             }
//           </div>
//         </div>
//       </Modal.Body>

//       <Modal.Footer className="border-0 pt-0">
//         <div className="d-flex gap-2 w-100 justify-content-end">
//           <button
//             className="action-button secondary"
//             onClick={handleClose}
//             disabled={isProcessing}
//           >
//             <X size={16} />
//             Avbryt
//           </button>

//           <button
//             className="action-button danger"
//             onClick={handleDelete}
//             disabled={isProcessing}
//           >
//             {isProcessing ? (
//               <>
//                 <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
//                 {allowFullDeletion ? "Raderar..." : "Annullerar..."}
//               </>
//             ) : (
//               <>
//                 <Trash2 size={16} />
//                 {allowFullDeletion ? "Radera Verifikat" : "Annullera Verifikat"}
//               </>
//             )}
//           </button>
//         </div>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default DeleteVerificationModal;


// import React, { useState, useEffect } from "react";
// import { Modal, Button, Table, Form, Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faInfoCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
// import axios from '../../../../services/AxiosInstance';
// import { getApiUrl } from "../../../../utils/ApiUtils";
// import swal from "sweetalert";

// // Helper function to format numbers as Swedish currency (SEK)
// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
// };

// const DeleteVerificationModal = ({ show, handleClose, verificationData, companyId, onVerificationDeleted }) => {
//   const [annulmentTransactions, setAnnulmentTransactions] = useState([]);
//   const [allowFullDeletion, setAllowFullDeletion] = useState(false);
//   const [isProcessing, setIsProcessing] = useState(false);

//   useEffect(() => {
//     generateAnnulmentTransactions();
//   }, [verificationData]);

//   // Generate annulment transactions
//   const generateAnnulmentTransactions = () => {
//     if (verificationData && verificationData.transactions) {
//       const reversedTransactions = verificationData.transactions.map((transaction) => ({
//         ...transaction,
//         transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
//       }));
//       setAnnulmentTransactions(reversedTransactions);
//     }
//   };

//   const handleDelete = async () => {
//     try {
//       setIsProcessing(true);

//       // Send DELETE request with the `allowFullDeletion` flag in the request body
//       await axios.delete(getApiUrl(`${companyId}/verifications/${verificationData.verification_id}`), {
//         data: { allowFullDeletion },  // Pass the checkbox value
//         withCredentials: true,
//       });

//       if (allowFullDeletion) {
//         swal("Raderat!", "Verifikatet är nu helt raderat", "success");
//       } else {
//         swal("Annulerat!", "Verifikatet har nu blivit annulerat", "success");
//       }

//       // Notify parent component that a verification was deleted/annulled
//       if (onVerificationDeleted) {
//         onVerificationDeleted();
//       }

//       handleClose();
//     } catch (error) {
//       console.error('Error deleting verification:', error);
//       swal("Fel", error.response?.data?.message || "Det gick inte att radera verifikatet", "error");
//     } finally {
//       setIsProcessing(false);
//     }
//   };

//   // Tooltip component
//   const renderTooltip = (props) => (
//     <Tooltip {...props}>
//       Annulering kommer att vända transaktionerna. Full radering ska endast användas under utveckling.
//     </Tooltip>
//   );

//   return (
//     <Modal show={show} onHide={handleClose} size="lg">
//       <Modal.Header closeButton>
//         <Modal.Title>Radera verifikat: {verificationData?.series}{verificationData?.number}</Modal.Title>
//       </Modal.Header>
//       <Modal.Body>
//         <p>För att radera ett verifikat så skapas ett nytt verifikat som tar bort effekten av det verifikat du vill radera.</p>

//         <Accordion defaultActiveKey="0">
//           <Accordion.Item eventKey="0">
//             <Accordion.Header>Verifikat som skall raderas</Accordion.Header>
//             <Accordion.Body>
//               <Table responsive>
//                 <thead>
//                   <tr>
//                     <th>Konto</th>
//                     <th>Debet</th>
//                     <th>Kredit</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {verificationData?.transactions?.map((transaction, index) => (
//                     <tr key={index}>
//                       <td>{transaction.account_number} - {transaction.account_name}</td>
//                       <td>{transaction.transaction_type === 'debit' ? formatCurrency(transaction.amount) : formatCurrency(0)}</td>
//                       <td>{transaction.transaction_type === 'credit' ? formatCurrency(transaction.amount) : formatCurrency(0)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </Table>
//             </Accordion.Body>
//           </Accordion.Item>

//           <Accordion.Item eventKey="1">
//             <Accordion.Header>Det nya verifikatet</Accordion.Header>
//             <Accordion.Body>
//               <Table responsive>
//                 <thead>
//                   <tr>
//                     <th>Konto</th>
//                     <th>Debet</th>
//                     <th>Kredit</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {annulmentTransactions.map((transaction, index) => (
//                     <tr key={index}>
//                       <td>{transaction.account_number} - {transaction.account_name}</td>
//                       <td>{transaction.transaction_type === 'debit' ? formatCurrency(transaction.amount) : formatCurrency(0)}</td>
//                       <td>{transaction.transaction_type === 'credit' ? formatCurrency(transaction.amount) : formatCurrency(0)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </Table>
//             </Accordion.Body>
//           </Accordion.Item>
//         </Accordion>

//         <Form.Group className="mt-4">
//           <Form.Check
//             type="checkbox"
//             label={
//               <span>
//                 Fullständig radering (Development Only)
//                 <OverlayTrigger
//                   placement="right"
//                   delay={{ show: 250, hide: 400 }}
//                   overlay={renderTooltip}
//                 >
//                   <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" />
//                 </OverlayTrigger>
//               </span>
//             }
//             checked={allowFullDeletion}
//             onChange={(e) => setAllowFullDeletion(e.target.checked)}
//             disabled={isProcessing}
//           />
//         </Form.Group>
//       </Modal.Body>
//       <Modal.Footer>
//         <Button variant="secondary" onClick={handleClose} disabled={isProcessing}>
//           Avbryt
//         </Button>
//         <Button variant="danger" onClick={handleDelete} disabled={isProcessing}>
//           {isProcessing ? (
//             <>
//               <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
//               {allowFullDeletion ? "Raderar..." : "Annullerar..."}
//             </>
//           ) : (
//             allowFullDeletion ? "Radera verifikat" : "Annullera verifikat"
//           )}
//         </Button>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default DeleteVerificationModal;